import React, { useState, useEffect, useRef } from 'react';
import { useI18n } from '../../i18n';
import {
  Building2, User, Image, Shield, FileSignature, ChevronRight, ChevronLeft,
  Search, Loader2, MapPin, Phone, Mail, Globe, Upload, X, Check, AlertTriangle,
  Briefcase, Users, Calendar, Lock, Wifi, Server, Key, Eye, EyeOff, Download, Monitor
} from 'lucide-react';
import { toast } from '../../utils/toastUtils';
import { workspaces as workspacesApi, businessRegistry, vpn } from '../../api/client';
import { downloadInstaller } from '../../api/agents';

// Country configuration for business registry lookup
const COUNTRY_CONFIG = {
  FR: { name: 'France', flag: '\u{1F1EB}\u{1F1F7}', identifierLabel: 'SIREN', identifierPlaceholder: '9 chiffres', identifierLength: 9, source: 'API Gouv FR' },
  BE: { name: 'Belgique', flag: '\u{1F1E7}\u{1F1EA}', identifierLabel: 'BCE', identifierPlaceholder: '10 chiffres', identifierLength: 10, source: 'Pappers' },
  GB: { name: 'United Kingdom', flag: '\u{1F1EC}\u{1F1E7}', identifierLabel: 'Company number', identifierPlaceholder: '8 characters', identifierLength: 8, source: 'Pappers' },
  CH: { name: 'Suisse', flag: '\u{1F1E8}\u{1F1ED}', identifierLabel: 'IDE', identifierPlaceholder: 'CHE-xxx.xxx.xxx', identifierLength: 15, source: 'Pappers' },
  IT: { name: 'Italy', flag: '\u{1F1EE}\u{1F1F9}', identifierLabel: 'Codice Fiscale', identifierPlaceholder: '11 or 16 characters', identifierLength: 16, source: 'Italian Business Register' },
  ES: { name: 'Spain', flag: '\u{1F1EA}\u{1F1F8}', identifierLabel: 'NIF/CIF', identifierPlaceholder: '9 characters', identifierLength: 9, source: 'InfoNIF' },
  LU: { name: 'Luxembourg', flag: '\u{1F1F1}\u{1F1FA}', identifierLabel: 'RCS', identifierPlaceholder: 'B123456', identifierLength: 7, source: 'LBR' }
};

const STEPS = [
  { id: 'company', label: 'Company', icon: Building2, description: 'Company information' },
  { id: 'contact', label: 'Contact', icon: User, description: 'Contact principal' },
  { id: 'logo', label: 'Logo', icon: Image, description: 'Visual identity' },
  { id: 'vpn', label: 'VPN', icon: Shield, description: 'Network configuration' },
  { id: 'contract', label: 'Contrat', icon: FileSignature, description: 'Validation finale' }
];

// VPN Protocol options
const VPN_PROTOCOLS = {
  openvpn: { name: 'OpenVPN', icon: '🔐', description: 'Fichier .ovpn standard', fileExt: '.ovpn' },
  wireguard: { name: 'WireGuard', icon: '⚡', description: 'VPN moderne et rapide', fileExt: '.conf' },
  l2tp: { name: 'L2TP/IPSec', icon: '🛡️', description: 'Compatible Windows natif', fileExt: null },
  agent: { name: 'SYLink Agent', icon: '🖥️', description: 'Tunnel via agent Windows', fileExt: null }
};

const WorkspaceWizard = ({ isOpen, onClose, onCreated }) => {
  const { t } = useI18n();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    company_identifier: '',
    siren: '',
    siret: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'France',
    country_code: 'FR',
    legal_form: '',
    naf_code: '',
    capital: '',
    employees_count: '',
    website: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    contact_position: '',
    logo_base64: null,
    contract_signed: false,
    vpn_config_id: null,
    vpn_type: null,
    vpn_name: null,
    vpn_protocol: 'openvpn',
    vpn_credentials: {},
    vpn_config_data: {}
  });

  const [loading, setLoading] = useState(false);
  const [registryLoading, setRegistryLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [vpnConfigs, setVpnConfigs] = useState({ externe: [], interne: [] });
  const [vpnLoading, setVpnLoading] = useState(false);
  const [showVpnPassword, setShowVpnPassword] = useState(false);
  const [vpnUploadLoading, setVpnUploadLoading] = useState(false);
  const [pendingVpnFile, setPendingVpnFile] = useState(null);  // VPN file to upload after workspace creation

  // Company search state
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchContainerRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const currentCountry = COUNTRY_CONFIG[formData.country_code] || COUNTRY_CONFIG.FR;

  // Fetch VPN configs on mount
  useEffect(() => {
    if (isOpen) {
      fetchVpnConfigs();
    }
  }, [isOpen]);

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchVpnConfigs = async () => {
    try {
      setVpnLoading(true);
      const configs = await vpn.getConfigs();
      setVpnConfigs(configs);
    } catch (error) {
      console.error('Error fetching VPN configs:', error);
    } finally {
      setVpnLoading(false);
    }
  };

  // Handle country change
  const handleCountryChange = (countryCode) => {
    const countryInfo = COUNTRY_CONFIG[countryCode];
    setFormData(prev => ({
      ...prev,
      country_code: countryCode,
      country: countryInfo.name,
      company_identifier: '',
      siren: '',
      siret: ''
    }));
  };

  // Handle company name search with debounce
  // Performs case-insensitive search by trying both original and uppercase queries in parallel
  const handleCompanySearch = (searchValue) => {
    setCompanySearchTerm(searchValue);

    if (searchValue.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      setSearchLoading(false);
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    setSearchLoading(true);

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const upperValue = searchValue.toUpperCase();
        const lowerValue = searchValue.toLowerCase();

        // Always search with multiple case variations in parallel for better results
        const searchPromises = [
          businessRegistry.search(formData.country_code, searchValue, null, 10).catch(() => [])
        ];

        // Add uppercase search if different from original
        if (searchValue !== upperValue) {
          searchPromises.push(
            businessRegistry.search(formData.country_code, upperValue, null, 10).catch(() => [])
          );
        }

        // Add lowercase search if different from both original and uppercase
        if (searchValue !== lowerValue && lowerValue !== upperValue) {
          searchPromises.push(
            businessRegistry.search(formData.country_code, lowerValue, null, 10).catch(() => [])
          );
        }

        // Wait for all searches to complete
        const allResults = await Promise.all(searchPromises);

        // Merge all results, avoiding duplicates by registration_number
        const seenIds = new Set();
        const mergedResults = [];

        for (const resultSet of allResults) {
          for (const result of (resultSet || [])) {
            if (result && result.registration_number && !seenIds.has(result.registration_number)) {
              seenIds.add(result.registration_number);
              mergedResults.push(result);
            }
          }
        }

        setSearchResults(mergedResults);
        setShowSearchResults(true);
      } catch (error) {
        console.error('Company search error:', error);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  };

  // Handle company selection from search results
  const handleSelectCompany = (company) => {
    setFormData(prev => ({
      ...prev,
      company_name: company.company_name || prev.company_name,
      siren: company.registration_number || prev.siren,
      siret: company.tax_id || prev.siret,
      address: company.address || prev.address,
      city: company.city || prev.city,
      postal_code: company.postal_code || prev.postal_code,
      country: company.country || prev.country,
      legal_form: company.legal_form || prev.legal_form,
      naf_code: company.activity_code ? `${company.activity_code} - ${company.activity_description || ''}` : prev.naf_code,
      capital: company.capital || prev.capital,
      employees_count: company.employees_count || prev.employees_count,
      website: company.website || prev.website,
      contact_email: company.contact_email || prev.contact_email,
      contact_phone: company.contact_phone || prev.contact_phone
    }));

    if (!formData.name && company.company_name) {
      setFormData(prev => ({
        ...prev,
        name: `${company.company_name} - Pentest ${new Date().getFullYear()}`
      }));
    }

    setCompanySearchTerm(company.company_name || '');
    setShowSearchResults(false);
    setSearchResults([]);
    toast.success('Informations entreprise chargees');
  };

  // Handle logo upload
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Le logo ne doit pas depasser 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      setLogoPreview(base64);
      setFormData(prev => ({ ...prev, logo_base64: base64 }));
    };
    reader.readAsDataURL(file);
  };

  // Handle VPN selection
  const handleVpnSelect = (config) => {
    // Clear pending file if selecting an existing config
    if (config) {
      setPendingVpnFile(null);
      setFormData(prev => ({
        ...prev,
        vpn_config_id: config.id,
        vpn_type: config.type,
        vpn_name: config.name,
        vpn_credentials: config.requires_auth ? prev.vpn_credentials : {}
      }));
    } else {
      setPendingVpnFile(null);
      setFormData(prev => ({
        ...prev,
        vpn_config_id: null,
        vpn_type: null,
        vpn_name: null,
        vpn_credentials: {}
      }));
    }
  };

  // Handle VPN file upload for internal VPN (OpenVPN or WireGuard)
  const handleVpnFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isOpenVPN = file.name.endsWith('.ovpn');
    const isWireGuard = file.name.endsWith('.conf');

    if (!isOpenVPN && !isWireGuard) {
      toast.error('Seuls les fichiers .ovpn (OpenVPN) ou .conf (WireGuard) sont acceptes');
      return;
    }

    if (file.size > 1024 * 1024) {  // 1MB max
      toast.error('Le fichier VPN ne doit pas depasser 1MB');
      return;
    }

    // Determine protocol from file extension
    const detectedProtocol = isOpenVPN ? 'openvpn' : 'wireguard';

    // Store file for upload after workspace creation
    setPendingVpnFile(file);

    // Update form to indicate a new VPN will be uploaded
    setFormData(prev => ({
      ...prev,
      vpn_config_id: 'pending_upload',  // Special marker
      vpn_type: 'interne',
      vpn_name: file.name.replace('.ovpn', '').replace('.conf', ''),
      vpn_protocol: detectedProtocol,
      vpn_credentials: {},
      vpn_config_data: {}
    }));

    toast.success(`File ${VPN_PROTOCOLS[detectedProtocol].name} "${file.name}" ready for upload`);
  };

  // Remove pending VPN file
  const handleRemovePendingVpn = () => {
    setPendingVpnFile(null);
    setFormData(prev => ({
      ...prev,
      vpn_config_id: null,
      vpn_type: null,
      vpn_name: null,
      vpn_credentials: {}
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error('Le nom du workspace est requis');
      return;
    }

    try {
      setLoading(true);

      // Clean and validate data before sending
      const cleanData = { ...formData };

      // SIREN must be exactly 9 digits or empty
      if (cleanData.siren && (cleanData.siren.length > 9 || !/^\d*$/.test(cleanData.siren))) {
        cleanData.siren = null;
      }

      // SIRET must be exactly 14 digits or empty
      if (cleanData.siret && (cleanData.siret.length > 14 || !/^\d*$/.test(cleanData.siret))) {
        cleanData.siret = null;
      }

      // Postal code must be max 10 chars
      if (cleanData.postal_code && cleanData.postal_code.length > 10) {
        cleanData.postal_code = cleanData.postal_code.substring(0, 10);
      }

      // NAF code must be max 10 chars
      if (cleanData.naf_code && cleanData.naf_code.length > 10) {
        cleanData.naf_code = cleanData.naf_code.substring(0, 10);
      }

      // Prepare workspace data with VPN protocol info
      const workspaceData = {
        ...cleanData,
        // Include VPN protocol and config_data for manual configurations
        vpn_protocol: cleanData.vpn_protocol,
        vpn_config_data: cleanData.vpn_config_data
      };

      // Create workspace first
      const workspace = await workspacesApi.create(workspaceData);

      // If there's a pending VPN file to upload, do it now with the workspace ID
      if (pendingVpnFile) {
        try {
          const vpnResult = await vpn.upload(pendingVpnFile, 'interne', workspace.id);
          // Update workspace with VPN config
          await workspacesApi.update(workspace.id, {
            vpn_config_id: vpnResult.config.id,
            vpn_type: 'interne',
            vpn_name: vpnResult.config.name,
            vpn_protocol: formData.vpn_protocol
          });
          toast.success(`VPN ${VPN_PROTOCOLS[formData.vpn_protocol]?.name || 'internal'} configured`);
        } catch (vpnError) {
          console.error('VPN upload error:', vpnError);
          toast.warning('Workspace created but error during VPN upload');
        }
      } else if (formData.vpn_config_id === 'pending_agent') {
        // Agent tunnel - just update workspace metadata
        try {
          await workspacesApi.update(workspace.id, {
            vpn_type: 'agent',
            vpn_name: 'SYLink Agent Tunnel',
            vpn_protocol: 'agent'
          });
          toast.success('Mode Agent SYLink configure - enregistrez l\'agent dans le menu Agents');
        } catch (vpnError) {
          console.error('Agent config error:', vpnError);
          toast.warning('Workspace cree mais erreur lors de la configuration Agent');
        }
      } else if (formData.vpn_config_id === 'pending_manual') {
        // Manual VPN configuration (WireGuard or L2TP)
        try {
          if (formData.vpn_protocol === 'wireguard' && formData.vpn_config_data) {
            // Create WireGuard config file and upload it
            const wgConfig = `[Interface]
PrivateKey = ${formData.vpn_credentials?.private_key || ''}
Address = ${formData.vpn_config_data.address || ''}
DNS = ${formData.vpn_config_data.dns || '8.8.8.8'}

[Peer]
PublicKey = ${formData.vpn_config_data.public_key || ''}
Endpoint = ${formData.vpn_config_data.endpoint || ''}
AllowedIPs = 0.0.0.0/0
PersistentKeepalive = 25`;

            const configName = `manual_wireguard_${Date.now()}`;
            const blob = new Blob([wgConfig], { type: 'text/plain' });
            const file = new File([blob], `${configName}.conf`, { type: 'text/plain' });

            // Upload via multi-protocol endpoint to create the file
            const uploadResult = await vpn.uploadMultiProtocol(file, workspace.id, 'wireguard');

            // Update workspace with correct config ID from upload result
            await workspacesApi.update(workspace.id, {
              vpn_config_id: uploadResult.config_id,
              vpn_type: 'interne',
              vpn_name: formData.vpn_name || 'WireGuard VPN',
              vpn_protocol: 'wireguard',
              vpn_config_data: formData.vpn_config_data
            });
          } else {
            // L2TP or other manual config - store in database only
            const configId = `int_${workspace.id}_manual_${formData.vpn_protocol}_${Date.now()}`;
            await workspacesApi.update(workspace.id, {
              vpn_config_id: configId,
              vpn_type: 'interne',
              vpn_name: formData.vpn_name || `${VPN_PROTOCOLS[formData.vpn_protocol]?.name} VPN`,
              vpn_protocol: formData.vpn_protocol,
              vpn_config_data: formData.vpn_config_data,
              vpn_credentials: formData.vpn_credentials
            });
          }
          toast.success(`VPN ${VPN_PROTOCOLS[formData.vpn_protocol]?.name} configured`);
        } catch (vpnError) {
          console.error('VPN config error:', vpnError);
          toast.warning('Workspace created but error during VPN configuration');
        }
      }

      toast.success('Workspace created successfully');
      onCreated(workspace);
      onClose();
    } catch (error) {
      console.error('Workspace creation error:', error.response?.data);
      const detail = error.response?.data?.detail;
      let errorMsg = error.message;

      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        // Pydantic validation errors - extract readable messages
        errorMsg = detail.map(e => `${e.loc?.slice(-1)?.[0] || 'champ'}: ${e.msg}`).join(', ');
      } else if (detail) {
        errorMsg = JSON.stringify(detail);
      }

      toast.error(`Erreur: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return formData.name && formData.company_name;
      case 1: return formData.contact_name && formData.contact_email;
      case 2: return true; // Logo is optional
      case 3: return true; // VPN is optional
      case 4: return true; // Contract can be signed later
      default: return true;
    }
  };

  const nextStep = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  if (!isOpen) return null;

  const selectedVpnConfig = formData.vpn_config_id
    ? [...(vpnConfigs.externe || []), ...(vpnConfigs.interne || [])].find(c => c.id === formData.vpn_config_id)
    : null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-brand-black-light rounded-2xl border border-brand-green/30 w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-800 bg-gradient-to-r from-brand-green/10 to-transparent">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                <Building2 className="w-7 h-7 text-brand-green" />
                Nouveau Client
              </h2>
              <p className="text-gray-400 mt-1">Assistant de creation de workspace</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-brand-black rounded-lg">
              <X className="w-6 h-6 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Steps Progress */}
        <div className="px-6 py-4 border-b border-gray-800 bg-brand-black/50">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-all ${
                  index === currentStep
                    ? 'bg-brand-green/20 border border-brand-green'
                    : index < currentStep
                      ? 'bg-green-500/10 border border-green-500/30'
                      : 'bg-brand-black border border-gray-700'
                }`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    index === currentStep
                      ? 'bg-brand-green text-black'
                      : index < currentStep
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-700 text-gray-400'
                  }`}>
                    {index < currentStep ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <step.icon className="w-4 h-4" />
                    )}
                  </div>
                  <div className="hidden md:block max-w-[100px] lg:max-w-[120px]">
                    <div className={`text-sm font-medium truncate ${
                      index === currentStep ? 'text-brand-green' : index < currentStep ? 'text-green-400' : 'text-gray-400'
                    }`} title={step.label}>
                      {step.label}
                    </div>
                    <div className="text-xs text-gray-500 truncate" title={step.description}>{step.description}</div>
                  </div>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`w-12 h-0.5 mx-2 ${
                    index < currentStep ? 'bg-green-500' : 'bg-gray-700'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Company */}
          {currentStep === 0 && (
            <div className="space-y-6">
              {/* Company Search */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Search className="w-5 h-5 text-blue-400" />
                    <span className="font-medium text-blue-400">Recherche Entreprise</span>
                  </div>
                  <span className="text-xs text-gray-500">via {currentCountry.source}</span>
                </div>

                {/* Country Selector */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {Object.entries(COUNTRY_CONFIG).map(([code, config]) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => handleCountryChange(code)}
                      className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 transition-colors ${
                        formData.country_code === code
                          ? 'bg-blue-600 text-white'
                          : 'bg-brand-black border border-gray-700 text-gray-400 hover:border-blue-500'
                      }`}
                    >
                      <span>{config.flag}</span>
                      <span className="hidden sm:inline">{config.name}</span>
                    </button>
                  ))}
                </div>

                {/* Company Search Input */}
                <div className="relative" ref={searchContainerRef}>
                  <input
                    type="text"
                    value={companySearchTerm}
                    onChange={(e) => handleCompanySearch(e.target.value)}
                    onFocus={() => searchResults.length > 0 && setShowSearchResults(true)}
                    className="w-full px-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none pr-10"
                    placeholder="Rechercher par nom d'entreprise..."
                  />
                  {searchLoading && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-400 animate-spin" />
                  )}

                  {/* Search Results Dropdown */}
                  {showSearchResults && searchResults.length > 0 && (
                    <div className="absolute z-50 w-full mt-2 bg-brand-black-light border border-gray-700 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                      {searchResults.map((company, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => handleSelectCompany(company)}
                          className="w-full px-4 py-3 text-left hover:bg-brand-green/10 border-b border-gray-800 last:border-b-0"
                        >
                          <div className="font-medium text-white">{company.company_name}</div>
                          <div className="text-xs text-gray-500 mt-1 flex items-center gap-3">
                            {company.registration_number && <span>{currentCountry.identifierLabel}: {company.registration_number}</span>}
                            {company.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{company.city}</span>}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Workspace Name */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Nom du workspace&nbsp;<span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                  placeholder={`Ex: Entreprise XYZ - Pentest ${new Date().getFullYear()}`}
                />
              </div>

              {/* Company Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Nom de l'entreprise&nbsp;<span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                    className="w-full px-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                    placeholder="Ex: Entreprise ABC"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    {currentCountry.identifierLabel}
                  </label>
                  <input
                    type="text"
                    value={formData.siren}
                    onChange={(e) => setFormData(prev => ({ ...prev, siren: e.target.value }))}
                    className="w-full px-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                    placeholder={currentCountry.identifierPlaceholder}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Forme juridique</label>
                  <input
                    type="text"
                    value={formData.legal_form}
                    onChange={(e) => setFormData(prev => ({ ...prev, legal_form: e.target.value }))}
                    className="w-full px-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white placeholder:text-gray-500 placeholder:italic focus:border-brand-green focus:outline-none"
                    placeholder="SAS, SARL, SA..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Effectifs</label>
                  <input
                    type="text"
                    value={formData.employees_count}
                    onChange={(e) => setFormData(prev => ({ ...prev, employees_count: e.target.value }))}
                    className="w-full px-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                    placeholder="Ex: 50, 100-200..."
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-400 mb-2">Adresse</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                    className="w-full px-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                    placeholder="Ex: 123 rue de la Paix"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Code postal</label>
                  <input
                    type="text"
                    value={formData.postal_code}
                    onChange={(e) => setFormData(prev => ({ ...prev, postal_code: e.target.value }))}
                    className="w-full px-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                    placeholder="Ex: 75001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Ville</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                    className="w-full px-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                    placeholder="Ex: Paris"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Site web</label>
                  <input
                    type="text"
                    value={formData.website}
                    onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                    className="w-full px-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Code NAF</label>
                  <input
                    type="text"
                    value={formData.naf_code}
                    onChange={(e) => setFormData(prev => ({ ...prev, naf_code: e.target.value }))}
                    className="w-full px-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                    placeholder="Ex: 62.01Z"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Contact */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-5 mb-6">
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5 text-purple-400" />
                  <span className="font-medium text-purple-400">Contact Principal</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">Personne responsable du projet de pentest</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Nom complet&nbsp;<span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.contact_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                    className="w-full px-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                    placeholder="First Last"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Poste / Fonction</label>
                  <input
                    type="text"
                    value={formData.contact_position}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact_position: e.target.value }))}
                    className="w-full px-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                    placeholder="RSSI, DSI, CTO..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Email&nbsp;<span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                      className="w-full pl-10 pr-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                      placeholder={t('auth.emailPlaceholder')}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Phone</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="tel"
                      value={formData.contact_phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, contact_phone: e.target.value }))}
                      className="w-full pl-10 pr-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                      placeholder="+33 6 12 34 56 78"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Logo */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-5 mb-6">
                <div className="flex items-center gap-2">
                  <Image className="w-5 h-5 text-cyan-400" />
                  <span className="font-medium text-cyan-400">Logo de l'entreprise</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">Sera utilise dans les rapports generes</p>
              </div>

              <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-gray-700 rounded-xl hover:border-brand-green/50 transition-colors">
                {logoPreview ? (
                  <div className="relative">
                    <img src={logoPreview} alt="Logo preview" className="max-w-64 max-h-64 object-contain rounded-lg" />
                    <button
                      type="button"
                      onClick={() => {
                        setLogoPreview(null);
                        setFormData(prev => ({ ...prev, logo_base64: null }));
                      }}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="cursor-pointer flex flex-col items-center gap-4">
                    <div className="p-6 bg-brand-black rounded-xl border border-gray-700">
                      <Upload className="w-12 h-12 text-gray-500" />
                    </div>
                    <div className="text-center">
                      <span className="text-brand-green font-medium">Cliquez pour telecharger</span>
                      <p className="text-sm text-gray-500 mt-1">PNG, JPG ou SVG (max 2MB)</p>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            </div>
          )}

          {/* Step 4: VPN */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-5 mb-6">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-green-400" />
                  <span className="font-medium text-green-400">Configuration VPN</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">Configure client network access for scans</p>
              </div>

              {vpnLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-brand-green" />
                </div>
              ) : (
                <div className="space-y-4">
                  {/* No VPN Option */}
                  <div
                    onClick={() => handleVpnSelect(null)}
                    className={`p-4 rounded-xl border cursor-pointer transition-all ${
                      !formData.vpn_config_id
                        ? 'border-brand-green bg-brand-green/10'
                        : 'border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        !formData.vpn_config_id ? 'border-brand-green' : 'border-gray-600'
                      }`}>
                        {!formData.vpn_config_id && <div className="w-3 h-3 bg-brand-green rounded-full" />}
                      </div>
                      <div>
                        <div className="font-medium text-white">Sans VPN</div>
                        <div className="text-sm text-gray-500">Scans sur IP publiques uniquement</div>
                      </div>
                    </div>
                  </div>

                  {/* VPN Protocol Selection */}
                  <div className="border-t border-gray-700 pt-4">
                    <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                      <Server className="w-4 h-4" /> VPN Interne - Choisir le protocole
                    </h4>

                    {/* Protocol Tabs */}
                    <div className="flex gap-2 mb-4">
                      {Object.entries(VPN_PROTOCOLS).map(([key, proto]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => {
                            // Reset when changing protocol
                            setPendingVpnFile(null);
                            setFormData(prev => ({
                              ...prev,
                              vpn_protocol: key,
                              vpn_config_id: null,
                              vpn_config_data: {},
                              vpn_credentials: {}
                            }));
                          }}
                          className={`flex-1 px-4 py-3 rounded-xl border text-center transition-all min-w-0 ${
                            formData.vpn_protocol === key
                              ? 'border-brand-green bg-brand-green/10 text-white'
                              : 'border-gray-700 hover:border-gray-600 text-gray-400'
                          }`}
                          title={`${proto.name} - ${proto.description}`}
                        >
                          <div className="text-xl mb-1">{proto.icon}</div>
                          <div className="font-medium text-sm truncate">{proto.name}</div>
                          <div className="text-xs text-gray-500 truncate">{proto.description}</div>
                        </button>
                      ))}
                    </div>

                    {/* OpenVPN Configuration */}
                    {formData.vpn_protocol === 'openvpn' && (
                      <div className="space-y-4">
                        {pendingVpnFile ? (
                          <div className={`p-4 rounded-xl border ${
                            formData.vpn_config_id === 'pending_upload'
                              ? 'border-brand-green bg-brand-green/10'
                              : 'border-gray-700'
                          }`}>
                            <div className="flex items-center gap-3">
                              <div className="w-5 h-5 rounded-full border-2 border-brand-green flex items-center justify-center">
                                <div className="w-3 h-3 bg-brand-green rounded-full" />
                              </div>
                              <div className="flex-1">
                                <div className="font-medium text-white flex items-center gap-2">
                                  <Wifi className="w-4 h-4 text-blue-400" />
                                  {pendingVpnFile.name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  OpenVPN - sera uploade a la creation du workspace
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={handleRemovePendingVpn}
                                className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-700 rounded-xl hover:border-blue-500/50 cursor-pointer transition-colors bg-brand-black/30">
                            <div className="p-3 bg-blue-500/10 rounded-full mb-3">
                              <Upload className="w-6 h-6 text-blue-400" />
                            </div>
                            <span className="text-blue-400 font-medium">Uploader un fichier .ovpn</span>
                            <span className="text-sm text-gray-500 mt-1">Configuration OpenVPN du client</span>
                            <input
                              type="file"
                              accept=".ovpn"
                              onChange={handleVpnFileUpload}
                              className="hidden"
                            />
                          </label>
                        )}

                        {/* OpenVPN Credentials */}
                        {pendingVpnFile && (
                          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                            <h4 className="text-sm font-medium text-blue-400 mb-3 flex items-center gap-2">
                              <Key className="w-4 h-4" /> Identifiants OpenVPN (optionnel)
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm text-gray-400 mb-2">Nom d'utilisateur</label>
                                <input
                                  type="text"
                                  value={formData.vpn_credentials.username || ''}
                                  onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    vpn_credentials: { ...prev.vpn_credentials, username: e.target.value }
                                  }))}
                                  className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                                  placeholder="Si requis par le VPN"
                                />
                              </div>
                              <div>
                                <label className="block text-sm text-gray-400 mb-2">Mot de passe</label>
                                <div className="relative">
                                  <input
                                    type={showVpnPassword ? 'text' : 'password'}
                                    value={formData.vpn_credentials.password || ''}
                                    onChange={(e) => setFormData(prev => ({
                                      ...prev,
                                      vpn_credentials: { ...prev.vpn_credentials, password: e.target.value }
                                    }))}
                                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none pr-10"
                                    placeholder="Si requis par le VPN"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setShowVpnPassword(!showVpnPassword)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-400"
                                  >
                                    {showVpnPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* WireGuard Configuration */}
                    {formData.vpn_protocol === 'wireguard' && (
                      <div className="space-y-4">
                        {/* File upload or manual config */}
                        <div className="flex gap-3 mb-4">
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, vpn_config_data: { ...prev.vpn_config_data, configMode: 'file' }}))}
                            className={`flex-1 px-4 py-2 rounded-lg border text-sm min-w-0 truncate ${
                              (formData.vpn_config_data.configMode || 'file') === 'file'
                                ? 'border-purple-500 bg-purple-500/10 text-purple-400'
                                : 'border-gray-700 text-gray-400 hover:border-gray-600'
                            }`}
                            title="Fichier .conf"
                          >
                            <Upload className="w-4 h-4 inline mr-2 flex-shrink-0" />
                            <span className="truncate">Fichier .conf</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, vpn_config_data: { ...prev.vpn_config_data, configMode: 'manual' }}))}
                            className={`flex-1 px-4 py-2 rounded-lg border text-sm min-w-0 truncate ${
                              formData.vpn_config_data.configMode === 'manual'
                                ? 'border-purple-500 bg-purple-500/10 text-purple-400'
                                : 'border-gray-700 text-gray-400 hover:border-gray-600'
                            }`}
                            title="Configuration manuelle"
                          >
                            <Key className="w-4 h-4 inline mr-2 flex-shrink-0" />
                            <span className="truncate">Configuration manuelle</span>
                          </button>
                        </div>

                        {(formData.vpn_config_data.configMode || 'file') === 'file' ? (
                          // File upload mode
                          pendingVpnFile ? (
                            <div className="p-4 rounded-xl border border-brand-green bg-brand-green/10">
                              <div className="flex items-center gap-3">
                                <div className="w-5 h-5 rounded-full border-2 border-brand-green flex items-center justify-center">
                                  <div className="w-3 h-3 bg-brand-green rounded-full" />
                                </div>
                                <div className="flex-1">
                                  <div className="font-medium text-white flex items-center gap-2">
                                    <Wifi className="w-4 h-4 text-purple-400" />
                                    {pendingVpnFile.name}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    WireGuard - sera uploade a la creation du workspace
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={handleRemovePendingVpn}
                                  className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ) : (
                            <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-700 rounded-xl hover:border-purple-500/50 cursor-pointer transition-colors bg-brand-black/30">
                              <div className="p-3 bg-purple-500/10 rounded-full mb-3">
                                <Upload className="w-6 h-6 text-purple-400" />
                              </div>
                              <span className="text-purple-400 font-medium">Uploader un fichier .conf</span>
                              <span className="text-sm text-gray-500 mt-1">Configuration WireGuard du client</span>
                              <input
                                type="file"
                                accept=".conf"
                                onChange={handleVpnFileUpload}
                                className="hidden"
                              />
                            </label>
                          )
                        ) : (
                          // Manual configuration mode
                          <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 space-y-4">
                            <h4 className="text-sm font-medium text-purple-400 flex items-center gap-2">
                              <Key className="w-4 h-4" /> Configuration WireGuard
                            </h4>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="md:col-span-2">
                                <label className="block text-sm text-gray-400 mb-2">Endpoint (serveur:port)&nbsp;<span className="text-red-400">*</span></label>
                                <input
                                  type="text"
                                  value={formData.vpn_config_data.endpoint || ''}
                                  onChange={(e) => {
                                    setFormData(prev => ({
                                      ...prev,
                                      vpn_config_id: 'pending_manual',
                                      vpn_type: 'interne',
                                      vpn_name: 'WireGuard VPN',
                                      vpn_config_data: { ...prev.vpn_config_data, endpoint: e.target.value }
                                    }));
                                  }}
                                  className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                                  placeholder="vpn.client.com:51820"
                                />
                              </div>
                              <div className="md:col-span-2">
                                <label className="block text-sm text-gray-400 mb-2">Server public key&nbsp;<span className="text-red-400">*</span></label>
                                <input
                                  type="text"
                                  value={formData.vpn_config_data.public_key || ''}
                                  onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    vpn_config_data: { ...prev.vpn_config_data, public_key: e.target.value }
                                  }))}
                                  className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-purple-500 focus:outline-none font-mono text-sm"
                                  placeholder="Public key Base64..."
                                />
                              </div>
                              <div>
                                <label className="block text-sm text-gray-400 mb-2">Adresse IP client</label>
                                <input
                                  type="text"
                                  value={formData.vpn_config_data.address || ''}
                                  onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    vpn_config_data: { ...prev.vpn_config_data, address: e.target.value }
                                  }))}
                                  className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                                  placeholder="10.0.0.2/24"
                                />
                              </div>
                              <div>
                                <label className="block text-sm text-gray-400 mb-2">DNS</label>
                                <input
                                  type="text"
                                  value={formData.vpn_config_data.dns || ''}
                                  onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    vpn_config_data: { ...prev.vpn_config_data, dns: e.target.value }
                                  }))}
                                  className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                                  placeholder="1.1.1.1"
                                />
                              </div>
                              <div>
                                <label className="block text-sm text-gray-400 mb-2">Allowed IPs</label>
                                <input
                                  type="text"
                                  value={formData.vpn_config_data.allowed_ips || '0.0.0.0/0'}
                                  onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    vpn_config_data: { ...prev.vpn_config_data, allowed_ips: e.target.value }
                                  }))}
                                  className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                                  placeholder="0.0.0.0/0"
                                />
                              </div>
                              <div>
                                <label className="block text-sm text-gray-400 mb-2">Pre-shared key (optional)</label>
                                <input
                                  type="text"
                                  value={formData.vpn_config_data.preshared_key || ''}
                                  onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    vpn_config_data: { ...prev.vpn_config_data, preshared_key: e.target.value }
                                  }))}
                                  className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-purple-500 focus:outline-none font-mono text-sm"
                                  placeholder="PSK key in Base64..."
                                />
                              </div>
                            </div>

                            {/* WireGuard Private Key */}
                            <div>
                              <label className="block text-sm text-gray-400 mb-2">Client private key&nbsp;<span className="text-red-400">*</span></label>
                              <div className="relative">
                                <input
                                  type={showVpnPassword ? 'text' : 'password'}
                                  value={formData.vpn_credentials.private_key || ''}
                                  onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    vpn_credentials: { ...prev.vpn_credentials, private_key: e.target.value }
                                  }))}
                                  className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-purple-500 focus:outline-none font-mono text-sm pr-10"
                                  placeholder="Private key Base64..."
                                />
                                <button
                                  type="button"
                                  onClick={() => setShowVpnPassword(!showVpnPassword)}
                                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-400"
                                >
                                  {showVpnPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* L2TP/IPSec Configuration */}
                    {formData.vpn_protocol === 'l2tp' && (
                      <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 space-y-4">
                        <h4 className="text-sm font-medium text-orange-400 flex items-center gap-2">
                          <Lock className="w-4 h-4" /> Configuration L2TP/IPSec
                        </h4>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="md:col-span-2">
                            <label className="block text-sm text-gray-400 mb-2">Adresse du serveur VPN&nbsp;<span className="text-red-400">*</span></label>
                            <input
                              type="text"
                              value={formData.vpn_config_data.server || ''}
                              onChange={(e) => {
                                setFormData(prev => ({
                                  ...prev,
                                  vpn_config_id: 'pending_manual',
                                  vpn_type: 'interne',
                                  vpn_name: 'L2TP VPN',
                                  vpn_config_data: { ...prev.vpn_config_data, server: e.target.value }
                                }));
                              }}
                              className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                              placeholder="vpn.client.com ou IP"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm text-gray-400 mb-2">Pre-Shared Key (PSK)&nbsp;<span className="text-red-400">*</span></label>
                            <div className="relative">
                              <input
                                type={showVpnPassword ? 'text' : 'password'}
                                value={formData.vpn_config_data.psk || ''}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  vpn_config_data: { ...prev.vpn_config_data, psk: e.target.value }
                                }))}
                                className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-orange-500 focus:outline-none pr-10"
                                placeholder="Cle pre-partagee IPSec"
                              />
                              <button
                                type="button"
                                onClick={() => setShowVpnPassword(!showVpnPassword)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-400"
                              >
                                {showVpnPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm text-gray-400 mb-2">Identifiant IPSec (optionnel)</label>
                            <input
                              type="text"
                              value={formData.vpn_config_data.ipsec_identifier || ''}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                vpn_config_data: { ...prev.vpn_config_data, ipsec_identifier: e.target.value }
                              }))}
                              className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                              placeholder="Laissez vide si non requis"
                            />
                          </div>
                        </div>

                        {/* L2TP Credentials */}
                        <div className="border-t border-orange-500/30 pt-4 mt-4">
                          <h5 className="text-sm font-medium text-orange-300 mb-3">Identifiants de connexion</h5>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm text-gray-400 mb-2">Nom d'utilisateur&nbsp;<span className="text-red-400">*</span></label>
                              <input
                                type="text"
                                value={formData.vpn_credentials.username || ''}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  vpn_credentials: { ...prev.vpn_credentials, username: e.target.value }
                                }))}
                                className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                                placeholder="Utilisateur VPN"
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-gray-400 mb-2">Mot de passe&nbsp;<span className="text-red-400">*</span></label>
                              <div className="relative">
                                <input
                                  type={showVpnPassword ? 'text' : 'password'}
                                  value={formData.vpn_credentials.password || ''}
                                  onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    vpn_credentials: { ...prev.vpn_credentials, password: e.target.value }
                                  }))}
                                  className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-orange-500 focus:outline-none pr-10"
                                  placeholder="Mot de passe VPN"
                                />
                                <button
                                  type="button"
                                  onClick={() => setShowVpnPassword(!showVpnPassword)}
                                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-400"
                                >
                                  {showVpnPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* SYLink Agent Configuration */}
                    {formData.vpn_protocol === 'agent' && (
                      <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4 space-y-4">
                        <h4 className="text-sm font-medium text-cyan-400 flex items-center gap-2">
                          <Monitor className="w-4 h-4" /> SYLink Agent - Tunnel Windows
                        </h4>
                        <div className="bg-brand-black/50 rounded-lg p-4 border border-gray-700">
                          <p className="text-sm text-gray-300">
                            Installez l'agent SYLink sur un poste Windows du reseau client pour creer un tunnel securise
                            vers l'infrastructure interne (similaire a ngrok). Aucun fichier VPN necessaire.
                          </p>
                        </div>
                        <div className="flex items-center gap-4 p-4 bg-cyan-500/5 border border-cyan-500/20 rounded-lg">
                          <div className="p-3 bg-cyan-500/10 rounded-full">
                            <Download className="w-6 h-6 text-cyan-400" />
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-white">SYLink Agent Installer</div>
                            <div className="text-xs text-gray-500">Windows 10/11 - Python 3.9+ requis</div>
                          </div>
                          <button onClick={downloadInstaller}
                            className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white font-medium rounded-lg transition-colors text-sm flex items-center gap-2">
                            <Download className="w-4 h-4" />
                            Telecharger l'Agent
                          </button>
                        </div>
                        <div className="space-y-3">
                          <h5 className="text-sm font-medium text-cyan-300">Instructions de configuration</h5>
                          <div className="space-y-2">
                            {[
                              { step: 1, text: "Telecharger et installer l'agent sur une machine Windows dans le reseau client" },
                              { step: 2, text: "Apres l'installation, noter l'Agent UID affiche (format: agent_xxxxxxxx)" },
                              { step: 3, text: "Enregistrer l'agent dans la plateforme via le menu Agents" },
                              { step: 4, text: "Configurer appsettings.json avec l'URL du serveur et le secret HMAC" },
                              { step: 5, text: "L'agent cree un tunnel chiffre - les scans pourront atteindre les cibles internes" }
                            ].map(({ step, text }) => (
                              <div key={step} className="flex items-start gap-3 p-2">
                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 text-xs font-bold flex items-center justify-center">
                                  {step}
                                </span>
                                <span className="text-sm text-gray-400">{text}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        {formData.vpn_config_id !== 'pending_agent' && (
                          <button type="button"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, vpn_config_id: 'pending_agent', vpn_type: 'agent', vpn_name: 'SYLink Agent Tunnel', vpn_protocol: 'agent' }));
                              toast.success('Mode Agent SYLink selectionne');
                            }}
                            className="w-full py-3 bg-cyan-600 hover:bg-cyan-500 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2">
                            <Check className="w-4 h-4" />
                            Utiliser SYLink Agent pour ce workspace
                          </button>
                        )}
                        {formData.vpn_config_id === 'pending_agent' && (
                          <div className="flex items-center gap-2 p-3 bg-cyan-500/20 border border-cyan-500/40 rounded-lg">
                            <Check className="w-4 h-4 text-cyan-400" />
                            <span className="text-sm text-cyan-300 font-medium">
                              SYLink Agent selectionne - configurez l'agent apres la creation du workspace
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* External VPNs */}
                  {vpnConfigs.externe?.length > 0 && (
                    <div className="border-t border-gray-700 pt-4">
                      <h4 className="text-sm font-medium text-gray-400 mb-2 flex items-center gap-2">
                        <Globe className="w-4 h-4" /> VPN Externes (partages)
                      </h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {vpnConfigs.externe.slice(0, 10).map(config => (
                          <div
                            key={config.id}
                            onClick={() => handleVpnSelect(config)}
                            className={`p-3 rounded-xl border cursor-pointer transition-all ${
                              formData.vpn_config_id === config.id
                                ? 'border-brand-green bg-brand-green/10'
                                : 'border-gray-700 hover:border-gray-600'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                formData.vpn_config_id === config.id ? 'border-brand-green' : 'border-gray-600'
                              }`}>
                                {formData.vpn_config_id === config.id && <div className="w-2 h-2 bg-brand-green rounded-full" />}
                              </div>
                              <div className="flex-1">
                                <div className="font-medium text-white text-sm">{config.name}</div>
                                <div className="text-xs text-gray-500">{config.provider || 'SYLink VPN'}</div>
                              </div>
                              {config.requires_auth && <Key className="w-3 h-3 text-yellow-500" />}
                            </div>
                          </div>
                        ))}
                        {vpnConfigs.externe.length > 10 && (
                          <p className="text-xs text-gray-500 text-center py-2">
                            + {vpnConfigs.externe.length - 10} autres VPN externes disponibles
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Info for external VPNs - credentials are automatic */}
                  {formData.vpn_config_id?.startsWith('ext_') && (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mt-4">
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-400" />
                        <span className="text-sm text-green-400 font-medium">
                          SYLink VPN credentials automatically configured
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        SYLink VPN account credentials are pre-configured in the system.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 5: Contract */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-5 mb-6">
                <div className="flex items-center gap-2">
                  <FileSignature className="w-5 h-5 text-orange-400" />
                  <span className="font-medium text-orange-400">Contrat de Pentest</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">Validation et signature du contrat</p>
              </div>

              {/* Summary */}
              <div className="bg-brand-black rounded-xl border border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Workspace summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-gray-500 text-sm">Workspace</span>
                    <p className="text-white font-medium">{formData.name || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">Entreprise</span>
                    <p className="text-white font-medium">{formData.company_name || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">Contact</span>
                    <p className="text-white font-medium">{formData.contact_name || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">Email</span>
                    <p className="text-white font-medium">{formData.contact_email || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">VPN</span>
                    <p className="text-white font-medium">
                      {formData.vpn_name || formData.vpn_config_id ? (
                        <>
                          {formData.vpn_name || 'VPN configured'}
                          {formData.vpn_protocol && (
                            <span className="ml-2 text-xs px-2 py-0.5 rounded bg-gray-700 text-gray-300">
                              {VPN_PROTOCOLS[formData.vpn_protocol]?.name || formData.vpn_protocol}
                            </span>
                          )}
                        </>
                      ) : 'Sans VPN'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">Logo</span>
                    <p className="text-white font-medium">{logoPreview ? 'Configured' : 'Not set'}</p>
                  </div>
                </div>
              </div>

              {/* Contract Status */}
              <div className={`p-4 rounded-xl border ${
                formData.contract_signed
                  ? 'border-green-500/30 bg-green-500/10'
                  : 'border-yellow-500/30 bg-yellow-500/10'
              }`}>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.contract_signed}
                    onChange={(e) => setFormData(prev => ({ ...prev, contract_signed: e.target.checked }))}
                    className="w-5 h-5 rounded border-gray-600 text-brand-green focus:ring-brand-green"
                  />
                  <div>
                    <span className={`font-medium ${formData.contract_signed ? 'text-green-400' : 'text-yellow-400'}`}>
                      {formData.contract_signed ? 'Contract signed' : 'Contract pending signature'}
                    </span>
                    <p className="text-sm text-gray-500 mt-1">
                      {formData.contract_signed
                        ? 'The client has signed the pentest contract'
                        : 'You can generate and have the contract signed later'}
                    </p>
                  </div>
                </label>
              </div>

              <div className="bg-brand-black/50 rounded-xl border border-gray-700 p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-400">
                      A pentest contract will be automatically generated and can be sent to the client for electronic signature.
                      Scans can only be started after the contract is validated.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-800 bg-brand-black/50 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={prevStep}
            disabled={currentStep === 0}
            className="flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-5 h-5" />
            Previous
          </button>

          <div className="flex flex-wrap items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white"
            >
              Annuler
            </button>

            {currentStep < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={nextStep}
                disabled={!canProceed()}
                className="flex items-center gap-2 px-6 py-2 bg-brand-green text-black font-medium rounded-lg hover:bg-brand-green-light disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
                <ChevronRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading || !canProceed()}
                className="flex items-center gap-2 px-6 py-2 bg-brand-green text-black font-medium rounded-lg hover:bg-brand-green-light disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    Create workspace
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkspaceWizard;
